import { useQuery } from '@apollo/client';
import { useScroll } from '@use-gesture/react';
import classNames from 'classnames';
import { animate, motion, useMotionValue } from 'framer-motion';
import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { Link, withRouter } from '../utils/router/v3Bridge';
import { isFeatureEnabled } from '../utils/features';
import PublicContentView from '../app/layout/PublicContentView';
import Button from '../components/Button';
import Caret from '../components/Caret';
import Form, { Field, Submit } from '../components/Form';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import PublicFooter from '../components/PublicFooter';
import TopicOrUserCollapsibleContent from '../components/TopicOrUserCollapsibleContent';
import NotFoundView from '../error/NotFoundView';
import type { PublicPostSearchResult } from '../graphql/generated/graphql';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import { parseSlug } from '../utils/router/useSlug';
import { deltaToText } from '../utils/text/deltaText';
import { publicTopicPath } from '../utils/url/topicPath';
import useQueryStringState from '../utils/useQueryStringState';
import PublicSearchResults from './PublicSearchResults';
import PublicTopicPosts from './PublicTopicPosts';
import styles from './PublicTopicView.module.scss';
import ResultsCountDisplay from './ResultsCountDisplay';
import TopicBreadcrumbs from './TopicBreadcrumbs';
import useTopbarHeight from '../topbar/useTopbarHeight';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

gql(`
  fragment PublicTopicFields on PublicTopic {
    id
    name
    description
    posts {
      id
      title
    }
    ancestors {
      id
      name
    }
  }
`);

const PUBLIC_TOPIC_QUERY = gql(`
  query PublicTopicView($id: ID!, $afterCursor: String) {
    topic: publicTopic(id: $id) {
      ...PublicTopicFields
      ...PublicContentView_topic
      descendants(after: $afterCursor, first: 10) {
        edges {
          node {
            id
            name
            description
            posts {
              id
              title
            }
            ancestors {
              id
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      ancestors {
        id
        ...TopicBreadcrumbs_topics
      }
    }
  }
`);

interface PublicTopicViewContentProps {
  topic: DocumentType<typeof PUBLIC_TOPIC_QUERY>['topic'];
  onFetchMore: (() => void) | null;
}

const PublicTopicViewContent = ({
  topic,
  onFetchMore,
}: PublicTopicViewContentProps) => {
  const edges = _.compact(topic.descendants?.edges);
  const descendants = _.compact(edges.map((edge) => edge?.node));

  return (
    <>
      {topic.posts.length > 0 && (
        <div className={styles.posts}>
          <PublicTopicPosts posts={topic.posts} />
        </div>
      )}
      {descendants.map((descendant) => {
        const descendantsOfCurrent = edges.filter(({ node }) =>
          node?.ancestors
            .map((ancestor) => ancestor.id)
            .includes(descendant.id),
        );
        const isDescendantsOfCurrentHavePosts = descendantsOfCurrent.some(
          ({ node }) => node && node.posts.length > 0,
        );
        if (descendant.posts.length === 0 && !isDescendantsOfCurrentHavePosts)
          return null;

        const depth =
          descendant.ancestors
            .map((ancestor) => ancestor.id)
            .indexOf(topic.id) + 1;
        let className;
        switch (depth) {
          case 1:
            className = styles.section;
            break;
          case 2:
            className = styles.subSection;
            break;
          case 3:
            className = styles.subSubSection;
            break;
          default:
            className = styles.subSubSubSection;
            break;
        }

        const descendantDescription = deltaToText(descendant.description);

        return (
          <div key={descendant.id} className={className}>
            <Link
              className={styles.sectionHeader}
              to={publicTopicPath(descendant)}
            >
              <div className={styles.sectionTitle}>{descendant.name}</div>
              <div className={styles.caret}>
                <Caret direction="right" size="small" block />
              </div>
            </Link>
            {descendantDescription && (
              <div className={styles.sectionDescription}>
                {descendantDescription}
              </div>
            )}
            {descendant.posts.length > 0 && (
              <div className={styles.sectionContent}>
                <PublicTopicPosts posts={descendant.posts} />
              </div>
            )}
          </div>
        );
      })}

      <InfiniteScrollWaypoint fetchMore={onFetchMore} />
    </>
  );
};

const SEARCH_RESULTS_LIMIT = 100;

const PUBLIC_SEARCH_QUERY = gql(`
  query PublicSearch($first: Int!, $query: String!, $afterCursor: String) {
    publicSearch(first: $first, after: $afterCursor, query: $query) {
      edges {
        node {
          ... on PublicPostSearchResult {
            ...PublicSearchResults_publicPostSearchResult
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

function usePublicSearch(query: string, skip: boolean) {
  const searchResult = useQuery(PUBLIC_SEARCH_QUERY, {
    variables: {
      query,
      first: SEARCH_RESULTS_LIMIT,
    },
    fetchPolicy: 'network-only',
    skip,
  });

  const fetchMoreSearchResults = ConnectionHelper.generateFetchMore(
    PUBLIC_SEARCH_QUERY,
    searchResult,
    'publicSearch',
  );

  const hasNextPage = Boolean(
    searchResult.data?.publicSearch?.pageInfo.hasNextPage,
  );

  const results = _.compact(
    searchResult.data?.publicSearch?.edges?.map((edge) => edge?.node),
  ).filter((node): node is PublicPostSearchResult => 'post' in node);

  return {
    hasNextPage,
    fetchMoreSearchResults,
    results,
    loading: searchResult.loading,
  };
}

const ANIMATION_CONFIG = {
  type: 'tween' as const,
  duration: 0.3,
};

interface StackedStickyHeaderProps {
  middleSection: React.ReactNode;
  topSection: React.ReactNode;
  bottomSection: React.ReactNode;
  enabled: boolean;
}

const StackedStickyHeader = ({
  middleSection,
  topSection,
  bottomSection,
  enabled,
}: StackedStickyHeaderProps) => {
  const topRef = useRef<HTMLDivElement>(null);
  const middleRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const translateY = useMotionValue(0);
  const [state, setState] = useState({
    isSticky: false,
    isBottomStuck: false,
    isTopStuck: false,
    topElHeight: 0,
  });

  const topbarHeight = useTopbarHeight();

  useScroll(
    ({ values: [, scrollTop], direction: [, direction] }) => {
      const topEl = topRef.current;
      const bottomEl = bottomRef.current;
      const middleEl = middleRef.current;

      if (topEl && bottomEl && middleEl && topbarHeight != null) {
        const { height: topElHeight } = topEl.getBoundingClientRect();
        const { height: middleElHeight } = middleEl.getBoundingClientRect();
        const { height: bottomElHeight } = bottomEl.getBoundingClientRect();
        const bottomElOffsetTop = middleEl.offsetTop + middleElHeight;
        const combinedHeight = topElHeight + bottomElHeight;

        const isBottomStuck = bottomEl.offsetTop !== bottomElOffsetTop;
        const isTopStuck = !isBottomStuck && topEl.offsetTop !== topbarHeight;

        if (scrollTop > bottomElOffsetTop + bottomElHeight - topbarHeight) {
          if (!state.isSticky) {
            translateY.set(-combinedHeight);
          }

          setState({
            isSticky: true,
            isBottomStuck,
            isTopStuck,
            topElHeight,
          });

          if (!translateY.isAnimating()) {
            animate(
              translateY,
              direction === 1 ? -combinedHeight : 0,
              ANIMATION_CONFIG,
            );
          }
        } else {
          setState((current) => ({
            ...current,
            isBottomStuck,
            isTopStuck,
          }));
        }

        if (
          state.isSticky &&
          !translateY.isAnimating() &&
          scrollTop <= bottomElOffsetTop + bottomElHeight - topbarHeight
        ) {
          animate(translateY, 0, ANIMATION_CONFIG);
        }

        if (scrollTop <= bottomElOffsetTop - topbarHeight) {
          translateY.stop();
          translateY.set(0);
        }

        if (scrollTop === 0) {
          setState({
            isBottomStuck: false,
            isSticky: false,
            isTopStuck: false,
            topElHeight,
          });
        }
      }
    },
    { target: window, enabled },
  );

  if (!enabled) {
    return (
      <>
        <div className={styles.stickyTopSection}>{topSection}</div>
        <div className={styles.stickyMiddleSection}>{middleSection}</div>
        {!!bottomSection && (
          <div className={styles.stickyBottomSection}>{bottomSection}</div>
        )}
      </>
    );
  }

  return (
    <>
      <motion.div
        ref={topRef}
        className={classNames(styles.stickyTopSection, {
          [styles.isSticky]: state.isSticky,
          [styles.isStuck]: state.isTopStuck,
        })}
        style={{ translateY }}
      >
        {topSection}
      </motion.div>

      <div className={styles.stickyMiddleSection} ref={middleRef}>
        {middleSection}
      </div>

      <motion.div
        ref={bottomRef}
        style={{
          top: (topbarHeight || 0) + state.topElHeight,
          translateY,
        }}
        className={classNames(styles.stickyBottomSection, {
          [styles.isSticky]: state.isSticky,
          [styles.isStuck]: state.isBottomStuck,
        })}
      >
        {bottomSection}
      </motion.div>
    </>
  );
};

interface BasePublicTopicViewProps {
  params: {
    topicSlug: string;
  };
  searchEnabled?: boolean;
}

export const BasePublicTopicView = ({
  params: { topicSlug },
  searchEnabled = isFeatureEnabled('public_search'),
}: BasePublicTopicViewProps) => {
  const [id] = parseSlug(topicSlug);
  const response = useQuery(PUBLIC_TOPIC_QUERY, {
    variables: { id },
  });
  const { data: { topic } = {}, loading } = response;
  const fetchMoreDescendants = ConnectionHelper.generateFetchMore(
    PUBLIC_TOPIC_QUERY,
    response,
    'topic.descendants',
  );

  const [isDescriptionCollapsed, setIsDescriptionCollapsed] = useState(false);
  const [search, setSearch] = useQueryStringState<string>('search', '');
  const isSearching = search !== '' && searchEnabled;
  const {
    results: searchResults,
    fetchMoreSearchResults,
    hasNextPage: hasNextSearchResultsPage,
    loading: isSearchLoading,
  } = usePublicSearch(search, !isSearching);

  if (!topic) {
    return loading ? null : <NotFoundView />;
  }

  return (
    <PublicContentView topic={topic}>
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <StackedStickyHeader
            enabled={searchEnabled}
            topSection={
              <div className={styles.titleContainer}>
                {topic.ancestors.length > 0 && (
                  <TopicBreadcrumbs topics={topic.ancestors} link />
                )}

                {isSearching ? (
                  <Button onClick={() => setSearch('')}>
                    <h1>{topic.name}</h1>
                  </Button>
                ) : (
                  <h1>{topic.name}</h1>
                )}
              </div>
            }
            middleSection={
              <>
                {topic.description && (
                  <h3 className={styles.description}>
                    <TopicOrUserCollapsibleContent
                      collapsed={isDescriptionCollapsed}
                      onCollapsedChange={() =>
                        setIsDescriptionCollapsed(!isDescriptionCollapsed)
                      }
                      content={topic.description}
                    />
                  </h3>
                )}
              </>
            }
            bottomSection={
              searchEnabled ? (
                <>
                  <Form<{ query: string }>
                    onSubmit={({ query }) => setSearch(query)}
                    initialValues={{ query: search }}
                    enableReinitialize
                  >
                    <div className={styles.searchFormFields}>
                      <Field
                        name="query"
                        placeholder="Search for knowledge..."
                      />
                      <Submit size="large">Search</Submit>
                    </div>
                  </Form>
                  {isSearching && !isSearchLoading && (
                    <div className={styles.resultsCountDisplayContainer}>
                      <ResultsCountDisplay
                        count={searchResults.length}
                        query={search}
                        hasNextPage={hasNextSearchResultsPage}
                      />
                    </div>
                  )}
                </>
              ) : null
            }
          />

          {isSearching ? (
            <PublicSearchResults
              query={search}
              results={searchResults}
              onFetchMore={fetchMoreSearchResults}
            />
          ) : (
            <PublicTopicViewContent
              topic={topic}
              onFetchMore={fetchMoreDescendants}
            />
          )}
        </div>
        <div className={styles.footerContainer}>
          <div className={styles.footerContainerInner}>
            <PublicFooter />
          </div>
        </div>
      </div>
    </PublicContentView>
  );
};

export default withRouter(BasePublicTopicView);
